/* @moldura padrao */
/**
 * page.tsx (usuarios) — CRUD de usuários (somente admin). Tema AdminUX (Bootstrap 5).
 *
 * O perfil é um PACOTE de permissões; por cima dele vêm as exceções da pessoa,
 * editadas em Perfis & Acessos — o item "Acessos" do menu ⋮ leva para lá já na
 * pessoa certa, em vez de repetir a mesma tela aqui dentro. O `+2 / −1` na
 * coluna Perfil mostra quem foge do próprio pacote. Foto aparece no avatar do topo.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { FormEvent } from 'react';
import { Modal } from 'react-bootstrap';
import { toast } from 'sonner';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/auth/auth-context';
import { CustomMolduraPagina } from '@/components/CustomMolduraPagina';

interface Usuario {
  id: number;
  nome: string;
  usuario: string;
  email: string | null;
  admin: boolean;
  autorizador: boolean;
  perfil_id: number | null;
  perfil: string | null;
  perfil_nome: string | null;
  foto: string | null;
  loja_id: number | null;
  loja_nome: string | null;
  status: 'ativo' | 'inativo';
  criado_em: string | null;
  atualizado_em: string | null;
  /** Quantas permissões esta pessoa tem a mais e a menos que o próprio perfil. */
  excecoes?: { mais: number; menos: number };
}

interface Perfil {
  id: number;
  codigo: string;
  nome: string;
}
interface Loja {
  id: number;
  nome: string;
}

function mensagemErro(err: unknown, padrao: string): string {
  return err instanceof ApiError ? err.message : padrao;
}

export function UsuariosPage() {
  const { usuario: eu, verComo } = useAuth();
  const navigate = useNavigate();

  /**
   * "Ver como": o admin passa a enxergar o app pelos olhos da pessoa, sem
   * pedir a senha dela — e a senha dela nem serviria depois que ela a trocasse.
   * É só leitura: o servidor recusa qualquer escrita enquanto o modo está
   * ligado, e uma tarja fixa no rodapé diz o tempo todo em nome de quem se está
   * olhando.
   */
  const entrarComo = async (u: Usuario) => {
    try {
      await verComo(u.id);
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Não foi possível.');
    }
  };

  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [perfis, setPerfis] = useState<Perfil[]>([]);
  const [lojas, setLojas] = useState<Loja[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const [formAberto, setFormAberto] = useState(false);
  const [editando, setEditando] = useState<Usuario | null>(null);
  const [senhaAlvo, setSenhaAlvo] = useState<Usuario | null>(null);

  async function carregar() {
    setCarregando(true);
    setErro(null);
    try {
      const dados = await api.get<Usuario[]>('/usuarios');
      setUsuarios(dados);
    } catch (err) {
      setErro(mensagemErro(err, 'Não foi possível carregar os usuários.'));
    } finally {
      setCarregando(false);
    }
  }

  useEffect(() => {
    carregar();
    api
      .get<{ perfis: Perfil[] }>('/configuracoes/perfis')
      .then((m) => setPerfis(m.perfis))
      .catch(() => undefined);
    api.get<Loja[]>('/lojas').then(setLojas).catch(() => undefined);
  }, []);

  function abrirNovo() {
    setEditando(null);
    setFormAberto(true);
  }

  function abrirEdicao(u: Usuario) {
    setEditando(u);
    setFormAberto(true);
  }

  async function alternarStatus(u: Usuario) {
    const novo = u.status === 'ativo' ? 'inativo' : 'ativo';
    try {
      await api.put(`/usuarios/${u.id}`, { status: novo });
      toast.success(
        `Usuário ${novo === 'ativo' ? 'ativado' : 'desativado'} com sucesso.`,
      );
      carregar();
    } catch (err) {
      toast.error(mensagemErro(err, 'Não foi possível alterar o status.'));
    }
  }

  return (
    <CustomMolduraPagina
      titulo="Usuários"
      descricao="Gerencie quem tem acesso ao sistema"
      acoes={
            <button className="btn btn-theme" onClick={abrirNovo}>
              <i className="bi bi-plus-lg me-1"></i> Novo usuário
            </button>
      }
    >

      <div className="card adminuiux-card">
        <div className="card-body">
          {carregando ? (
            <div className="d-flex align-items-center justify-content-center gap-2 py-5 text-secondary">
              <span className="spinner-border spinner-border-sm" /> Carregando…
            </div>
          ) : erro ? (
            <div className="py-5 text-center text-danger">{erro}</div>
          ) : (
            <div className="table-responsive">
              <table className="table align-middle table-hover mb-0">
                <thead>
                  <tr className="text-secondary small text-uppercase">
                    <th>Usuário</th>
                    <th>Nome</th>
                    <th>Perfil</th>
                    <th>Loja</th>
                    <th>Status</th>
                    <th className="text-end">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {usuarios.map((u) => (
                    // A LINHA INTEIRA ABRE O USUÁRIO, como na lista do irmão.
                    // Obrigar a mirar no menu de três pontinhos para uma ação
                    // que é a razão de a linha existir é atrito à toa — e o
                    // alvo do dedo, no celular, é a linha, não o ícone.
                    <tr
                      key={u.id}
                      onClick={() => abrirEdicao(u)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td className="fw-medium">
                        <div className="d-flex align-items-center gap-2">
                          {u.foto ? (
                            <img
                              src={u.foto}
                              alt={u.nome}
                              className="rounded-circle object-fit-cover"
                              style={{ width: '1.75rem', height: '1.75rem' }}
                            />
                          ) : (
                            <span
                              className="rounded-circle bg-light d-flex align-items-center justify-content-center text-secondary small"
                              style={{ width: '1.75rem', height: '1.75rem' }}
                            >
                              {u.nome.charAt(0).toUpperCase()}
                            </span>
                          )}
                          {u.usuario}
                        </div>
                      </td>
                      <td>{u.nome}</td>
                      <td>
                        <div className="d-flex flex-wrap align-items-center gap-1">
                          <span className="badge text-bg-secondary">
                            {u.perfil_nome ?? '—'}
                          </span>
                          {u.autorizador && (
                            <span className="badge text-bg-info">
                              <i className="bi bi-stamp me-1"></i>
                              Autorizador
                            </span>
                          )}
                          {/* Quem foge do próprio perfil se anuncia aqui: sem
                              isso, só se descobriria abrindo um a um. */}
                          {!!u.excecoes?.mais && (
                            <span
                              className="badge text-bg-success"
                              title={`${u.excecoes.mais} ${u.excecoes.mais === 1 ? 'permissão concedida' : 'permissões concedidas'} além do perfil`}
                            >
                              +{u.excecoes.mais}
                            </span>
                          )}
                          {!!u.excecoes?.menos && (
                            <span
                              className="badge text-bg-danger"
                              title={`${u.excecoes.menos} ${u.excecoes.menos === 1 ? 'permissão revogada' : 'permissões revogadas'} do perfil`}
                            >
                              −{u.excecoes.menos}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="text-secondary">{u.loja_nome ?? '—'}</td>
                      <td>
                        <span
                          className={`badge ${
                            u.status === 'ativo'
                              ? 'text-bg-success'
                              : 'text-bg-danger'
                          }`}
                        >
                          {u.status === 'ativo' ? 'Ativo' : 'Inativo'}
                        </span>
                      </td>
                      {/* O menu de ações não pode abrir o modal junto: o clique
                          para aqui e não sobe para a linha. */}
                      <td className="text-end" onClick={(e) => e.stopPropagation()}>
                        {/* Fora do menu de três pontinhos de propósito: é a
                            ação que se usa no meio de uma dúvida ("o que ela
                            está vendo?"), e esconder custa dois cliques. Não
                            aparece na própria linha — "ver como" você mesmo
                            não faz sentido, e o servidor recusaria. */}
                        {eu?.admin && u.id !== eu.id && u.status === 'ativo' && (
                          <button
                            type="button"
                            className="btn btn-link btn-square text-secondary"
                            onClick={() => entrarComo(u)}
                            title={`Ver o app como ${u.nome.split(' ')[0]}`}
                          >
                            <i className="bi bi-eye-fill"></i>
                          </button>
                        )}
                        <div className="dropdown d-inline-block">
                          <button
                            type="button"
                            className="btn btn-link btn-square"
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                          >
                            <i className="bi bi-three-dots-vertical"></i>
                          </button>
                          <ul className="dropdown-menu dropdown-menu-end">
                            <li>
                              <button
                                type="button"
                                className="dropdown-item"
                                onClick={() => abrirEdicao(u)}
                              >
                                <i className="bi bi-pencil me-2"></i>Editar
                              </button>
                            </li>
                            <li>
                              <button
                                type="button"
                                className="dropdown-item"
                                onClick={() =>
                                  navigate(`/configuracoes/perfis?pessoa=${u.id}`)
                                }
                              >
                                <i className="bi bi-shield-lock me-2"></i>Acessos
                              </button>
                            </li>
                            <li>
                              <button
                                type="button"
                                className="dropdown-item"
                                onClick={() => setSenhaAlvo(u)}
                              >
                                <i className="bi bi-key me-2"></i>Redefinir senha
                              </button>
                            </li>
                            <li>
                              <button
                                type="button"
                                className="dropdown-item"
                                onClick={() => alternarStatus(u)}
                              >
                                <i className="bi bi-power me-2"></i>
                                {u.status === 'ativo' ? 'Desativar' : 'Ativar'}
                              </button>
                            </li>
                          </ul>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {usuarios.length === 0 && (
                    <tr>
                      <td colSpan={6} className="py-5 text-center text-secondary">
                        Nenhum usuário cadastrado.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <UsuarioFormDialog
        aberto={formAberto}
        usuario={editando}
        perfis={perfis}
        lojas={lojas}
        onFechar={() => setFormAberto(false)}
        onSalvo={() => {
          setFormAberto(false);
          carregar();
        }}
      />

      <SenhaDialog
        usuario={senhaAlvo}
        onFechar={() => setSenhaAlvo(null)}
        onSalvo={() => setSenhaAlvo(null)}
      />

    </CustomMolduraPagina>
  );
}

/* ─── Dialog de criar/editar ─────────────────────────────────────────────── */

interface FormDialogProps {
  aberto: boolean;
  usuario: Usuario | null;
  perfis: Perfil[];
  lojas: Loja[];
  onFechar: () => void;
  onSalvo: () => void;
}

function UsuarioFormDialog({
  aberto,
  usuario,
  perfis,
  lojas,
  onFechar,
  onSalvo,
}: FormDialogProps) {
  const edicao = usuario !== null;

  const [nome, setNome] = useState('');
  const [login, setLogin] = useState('');
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [perfilId, setPerfilId] = useState<string>('');
  const [lojaId, setLojaId] = useState<string>('');
  const [autorizador, setAutorizador] = useState(false);
  const [status, setStatus] = useState<'ativo' | 'inativo'>('ativo');
  // foto: preview (url) + arquivo relativo a enviar quando trocada
  const [fotoPreview, setFotoPreview] = useState<string | null>(null);
  const [fotoArquivo, setFotoArquivo] = useState<string | null>(null);
  const [enviandoFoto, setEnviandoFoto] = useState(false);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (aberto) {
      setNome(usuario?.nome ?? '');
      setLogin(usuario?.usuario ?? '');
      setEmail(usuario?.email ?? '');
      setSenha('');
      setPerfilId(usuario?.perfil_id ? String(usuario.perfil_id) : '');
      setLojaId(usuario?.loja_id ? String(usuario.loja_id) : '');
      setAutorizador(usuario?.autorizador ?? false);
      setStatus(usuario?.status ?? 'ativo');
      setFotoPreview(usuario?.foto ?? null);
      setFotoArquivo(null);
    }
  }, [aberto, usuario]);

  async function enviarFoto(files: FileList | null) {
    if (!files || files.length === 0) return;
    setEnviandoFoto(true);
    try {
      const fd = new FormData();
      fd.append('arquivo', files[0]);
      const r = await api.upload<{ arquivo: string; url: string }>(
        '/usuarios/fotos',
        fd,
      );
      setFotoArquivo(r.arquivo);
      setFotoPreview(r.url);
    } catch (err) {
      toast.error(mensagemErro(err, 'Falha ao enviar a foto.'));
    } finally {
      setEnviandoFoto(false);
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSalvando(true);
    try {
      if (edicao && usuario) {
        const payload: Record<string, unknown> = {
          nome,
          email: email.trim() || null,
          perfil_id: perfilId ? Number(perfilId) : null,
          loja_id: lojaId ? Number(lojaId) : null,
          autorizador,
          status,
        };
        if (fotoArquivo) payload.foto = fotoArquivo;
        await api.put(`/usuarios/${usuario.id}`, payload);
        toast.success('Usuário atualizado.');
      } else {
        await api.post('/usuarios', {
          nome,
          usuario: login.trim(),
          email: email.trim() || null,
          senha,
          perfil_id: perfilId ? Number(perfilId) : null,
          loja_id: lojaId ? Number(lojaId) : null,
          autorizador,
          foto: fotoArquivo,
          status,
        });
        toast.success('Usuário criado.');
      }
      onSalvo();
    } catch (err) {
      toast.error(mensagemErro(err, 'Não foi possível salvar o usuário.'));
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Modal className="casaa-modal" show={aberto} onHide={onFechar} centered>
      <form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title as="h5">
            {edicao ? 'Editar usuário' : 'Novo usuário'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-secondary small">
            {edicao
              ? 'Atualize os dados do usuário.'
              : 'Preencha os dados para criar um novo acesso.'}
          </p>
          <div className="d-flex flex-column gap-3">
            {/* Foto */}
            <div className="d-flex align-items-center gap-3">
              {fotoPreview ? (
                <img
                  src={fotoPreview}
                  alt="Foto"
                  className="rounded-circle object-fit-cover border"
                  style={{ width: '3.5rem', height: '3.5rem' }}
                />
              ) : (
                <span
                  className="rounded-circle bg-light d-flex align-items-center justify-content-center text-secondary"
                  style={{ width: '3.5rem', height: '3.5rem' }}
                >
                  {(nome || '?').charAt(0).toUpperCase()}
                </span>
              )}
              <label className="btn btn-link p-0 text-decoration-none">
                <span className="d-inline-flex align-items-center gap-2 small">
                  {enviandoFoto ? (
                    <span className="spinner-border spinner-border-sm" />
                  ) : (
                    <i className="bi bi-camera"></i>
                  )}
                  {fotoPreview ? 'Trocar foto' : 'Adicionar foto'}
                </span>
                <input
                  type="file"
                  accept="image/*"
                  className="d-none"
                  disabled={enviandoFoto}
                  onChange={(e) => {
                    enviarFoto(e.target.files);
                    e.target.value = '';
                  }}
                />
              </label>
            </div>

            <div className="form-floating">
              <input
                id="f-nome"
                className="form-control"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                required
                placeholder="Ex.: Diogo"
              />
              <label htmlFor="f-nome">Nome de exibição</label>
            </div>

            <div>
              <div className="form-floating">
                <input
                  id="f-usuario"
                  className="form-control"
                  value={login}
                  onChange={(e) => setLogin(e.target.value)}
                  disabled={edicao}
                  required
                  minLength={3}
                  placeholder="Usuário (login)"
                />
                <label htmlFor="f-usuario">Usuário (login)</label>
              </div>
              {edicao && (
                <span className="text-secondary small">
                  O nome de usuário não pode ser alterado.
                </span>
              )}
            </div>

            <div className="row g-3">
              <div className="col-6">
                <div className="form-floating">
                  <select
                    id="f-perfil"
                    className="form-select"
                    value={perfilId}
                    onChange={(e) => setPerfilId(e.target.value)}
                  >
                    <option value="">Selecione</option>
                    {perfis.map((p) => (
                      <option key={p.id} value={String(p.id)}>
                        {p.nome}
                      </option>
                    ))}
                  </select>
                  <label htmlFor="f-perfil">Perfil</label>
                </div>
              </div>
              <div className="col-6">
                <div className="form-floating">
                  <select
                    id="f-loja"
                    className="form-select"
                    value={lojaId}
                    onChange={(e) => setLojaId(e.target.value)}
                  >
                    <option value="">Selecione</option>
                    {lojas.map((l) => (
                      <option key={l.id} value={String(l.id)}>
                        {l.nome}
                      </option>
                    ))}
                  </select>
                  <label htmlFor="f-loja">Loja padrão</label>
                </div>
              </div>
            </div>

            <div className="form-floating">
              <input
                id="f-email"
                type="email"
                className="form-control"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="pode preencher depois"
              />
              <label htmlFor="f-email">E-mail (opcional)</label>
            </div>

            {!edicao && (
              <div className="form-floating">
                <input
                  id="f-senha"
                  type="password"
                  className="form-control"
                  value={senha}
                  onChange={(e) => setSenha(e.target.value)}
                  required
                  minLength={6}
                  placeholder="mínimo 6 caracteres"
                />
                <label htmlFor="f-senha">Senha</label>
              </div>
            )}

            {edicao && (
              <div className="form-floating">
                <select
                  id="f-status"
                  className="form-select"
                  value={status}
                  onChange={(e) =>
                    setStatus(e.target.value as 'ativo' | 'inativo')
                  }
                >
                  <option value="ativo">Ativo</option>
                  <option value="inativo">Inativo</option>
                </select>
                <label htmlFor="f-status">Status</label>
              </div>
            )}

            <div className="d-flex align-items-center justify-content-between rounded border p-3">
              <div className="d-flex flex-column">
                <label htmlFor="f-autorizador" className="fw-medium mb-0">
                  Autorizador
                </label>
                <span className="text-secondary small">
                  Pode autorizar ou rejeitar Ordens de Serviço.
                </span>
              </div>
              <div className="form-check form-switch mb-0">
                <input
                  id="f-autorizador"
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  checked={autorizador}
                  onChange={(e) => setAutorizador(e.target.checked)}
                />
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-link"
            onClick={onFechar}
            disabled={salvando}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="btn btn-theme"
            disabled={salvando || enviandoFoto}
          >
            {salvando && (
              <span className="spinner-border spinner-border-sm me-1" />
            )}
            {edicao ? 'Salvar' : 'Criar'}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
}

/* ─── Dialog de redefinir senha ──────────────────────────────────────────── */

interface SenhaDialogProps {
  usuario: Usuario | null;
  onFechar: () => void;
  onSalvo: () => void;
}

function SenhaDialog({ usuario, onFechar, onSalvo }: SenhaDialogProps) {
  const [senha, setSenha] = useState('');
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (usuario) setSenha('');
  }, [usuario]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!usuario) return;
    setSalvando(true);
    try {
      await api.patch(`/usuarios/${usuario.id}/senha`, { senha });
      toast.success('Senha redefinida.');
      onSalvo();
    } catch (err) {
      toast.error(mensagemErro(err, 'Não foi possível redefinir a senha.'));
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Modal className="casaa-modal" show={usuario !== null} onHide={onFechar} centered size="sm">
      <form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title as="h5">Redefinir senha</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-secondary small">
            Nova senha para <strong>{usuario?.usuario}</strong>.
          </p>
          <div className="form-floating">
            <input
              id="s-senha"
              type="password"
              className="form-control"
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              required
              minLength={6}
              placeholder="mínimo 6 caracteres"
              autoFocus
            />
            <label htmlFor="s-senha">Nova senha</label>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-link"
            onClick={onFechar}
            disabled={salvando}
          >
            Cancelar
          </button>
          <button type="submit" className="btn btn-theme" disabled={salvando}>
            {salvando && (
              <span className="spinner-border spinner-border-sm me-1" />
            )}
            Redefinir
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
}
